/**
 * Loops index route (OL-312) — eagerly registers `/orca/loops` and re-applies
 * its OWN guard; the page component, its atoms and the frame are deferred to
 * `index.lazy.tsx` so nothing the index renders lands in the always-loaded
 * chunk (`packages/web/budgets.json`, "Web Bundle Budgets"). The sheets that
 * chunk's markup depends on come from `head()` here instead, as `?url` links.
 *
 * `orcaloops.ai/loops` rewrites onto this path — every orcaloops.ai path does,
 * because `/orca` is a WILDCARD domain prefix — which is why the file lives
 * under `routes/orca/` and not a bare `routes/loops/` the rewrite could never
 * reach.
 *
 * **`requireAuth`, never `requireAdmin`** — the canvas route's pattern exactly.
 * `/orca/loops` is an `ORCA_PUBLIC_ROOTS` entry so the `/orca` layout guard
 * steps aside on the ADMIN check; this guard puts a session back in its place.
 * A person reading their own list of loops is not an operator, and the admin
 * guard would bounce the page's only audience to `/access-denied`. The session
 * is necessary but not sufficient: `loops.list` resolves the owning customer
 * from the actor, so a member only ever sees their own.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import { loopShellStylesheetLinks } from "~/lib/loop-shell-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const loopIndexBeforeLoad = async () => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  return { auth };
};

/**
 * The route's `head()`: the orcaloops frame's sheet.
 * Extracted so it is unit-testable without the route machinery, exactly as
 * {@link loopIndexBeforeLoad} is.
 *
 * @since 1.0.0
 */
export const loopIndexHead = () => ({ links: loopShellStylesheetLinks });

export const Route = createFileRoute("/orca/loops/")({
  beforeLoad: loopIndexBeforeLoad,
  head: loopIndexHead,
});
